import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import ExpertAvatarUpload from '@/components/ExpertAvatarUpload';
import { useExpertProfile } from '@/hooks/useExpertProfile';
import { useUpdateExpertProfile } from '@/hooks/useUpdateExpertProfile';
import type { ExpertProfileFormData, ExpertProfileFormErrors, UploadedAvatar } from '@/types/expert-profile';

function validateForm(data: ExpertProfileFormData): ExpertProfileFormErrors {
  const errors: ExpertProfileFormErrors = {};

  if (!data.name.trim()) errors.name = 'Name is required';
  else if (data.name.length > 255) errors.name = 'Name must be 255 characters or less';

  if (!data.subtitle.trim()) errors.subtitle = 'Subtitle is required';
  else if (data.subtitle.length > 255) errors.subtitle = 'Subtitle must be 255 characters or less';

  return errors;
}

export default function EditExpertProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: profileData, isLoading, error } = useExpertProfile(id!);
  const updateMutation = useUpdateExpertProfile(id!);

  const [formData, setFormData] = useState<ExpertProfileFormData | null>(null);
  const [errors, setErrors] = useState<ExpertProfileFormErrors>({});

  useEffect(() => {
    document.title = 'Edit Expert Profile | Admin Dashboard';
    return () => {
      document.title = 'Admin Dashboard';
    };
  }, []);

  useEffect(() => {
    if (profileData) {
      const profile = profileData.data;
      const avatar =
        profile.avatarFileId && profile.avatarUrl
          ? { documentId: profile.avatarFileId, viewUrl: profile.avatarUrl, fileType: 'jpg' }
          : null;

      setFormData({
        name: profile.name,
        subtitle: profile.subtitle,
        avatar,
        avatarFileId: profile.avatarFileId,
      });
    }
  }, [profileData]);

  if (isLoading || !formData) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-pine-pine" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-card p-6">
        <p className="text-red-400">Failed to load expert profile</p>
        <button
          onClick={() => navigate('/admin/expert-profiles')}
          className="mt-4 text-pine-pine"
        >
          Back to Expert Profiles
        </button>
      </div>
    );
  }

  const handleFieldChange = (field: keyof ExpertProfileFormData, value: unknown) => {
    setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
    if (errors[field as keyof ExpertProfileFormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleAvatarChange = (avatar: UploadedAvatar | null) => {
    setFormData((prev) =>
      prev ? { ...prev, avatar, avatarFileId: avatar?.documentId ?? null } : null
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData) return;

    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error('Please fix the form errors');
      return;
    }

    try {
      await updateMutation.mutateAsync(formData);
      toast.success('Expert profile updated successfully!');
      navigate(`/admin/expert-profiles/${id}`);
    } catch (err) {
      console.error('Failed to update expert profile:', err);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Back button */}
      <button
        onClick={() => navigate(`/admin/expert-profiles/${id}`)}
        className="flex items-center gap-2 text-pine-ink-soft hover:text-pine-ink transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>Back to Expert Profile</span>
      </button>

      <h1 className="text-pine-display font-display text-pine-ink">Edit Expert Profile</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info Card */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">
                Name <span className="text-red-400">*</span>
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                placeholder="e.g. Dr. Sarah Johnson"
              />
              {errors.name && <p className="text-sm text-red-400">{errors.name}</p>}
            </div>

            {/* Subtitle */}
            <div className="space-y-2">
              <Label htmlFor="subtitle">
                Subtitle <span className="text-red-400">*</span>
              </Label>
              <Input
                id="subtitle"
                value={formData.subtitle}
                onChange={(e) => handleFieldChange('subtitle', e.target.value)}
                placeholder="e.g. Child Psychologist & Play Specialist"
              />
              {errors.subtitle && <p className="text-sm text-red-400">{errors.subtitle}</p>}
            </div>
          </CardContent>
        </Card>

        {/* Avatar Card */}
        <Card>
          <CardHeader>
            <CardTitle>Avatar</CardTitle>
          </CardHeader>
          <CardContent>
            <ExpertAvatarUpload
              value={formData.avatar}
              onChange={handleAvatarChange}
            />
            {errors.avatar && <p className="text-sm text-red-400 mt-2">{errors.avatar}</p>}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <button
            type="submit"
            disabled={updateMutation.isPending}
            className="admin-button-primary"
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            type="button"
            onClick={() => navigate(`/admin/expert-profiles/${id}`)}
            className="px-6 py-2.5 rounded-lg border border-pine-line text-pine-ink-soft hover:bg-pine-hover-button transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
